// <mini-cart-drawer> — sitewide slide-out cart. Opens on any "cart:updated"
// event (dispatched after a successful /cart/add.js call elsewhere in the
// theme) and hijacks clicks on links to /cart so they open the drawer
// instead of a full page load. Renders entirely from /cart.js so it can't
// go stale between pages.
class MiniCartDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('[data-mcd-overlay]');
    this.panel = this.querySelector('.mcd-panel');
    this.body = this.querySelector('[data-mcd-body]');
    this.subtotalEl = this.querySelector('[data-mcd-subtotal]');
    this.suggestWrap = this.querySelector('[data-mcd-suggest]');
    this.suggestGrid = this.querySelector('[data-mcd-suggest-grid]');
    this.closeBtn = this.querySelector('[data-mcd-close]');
    this.cart = null;

    this.closeBtn.addEventListener('click', this.close.bind(this));
    this.overlay.addEventListener('click', this.close.bind(this));
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') this.close();
    }.bind(this));

    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]');
      if (!a || a.hasAttribute('data-mcd-view-cart')) return;
      var url;
      try { url = new URL(a.href, window.location.origin); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;
      var path = url.pathname.replace(/\/$/, '') || '/';
      if (path === '/cart') {
        e.preventDefault();
        this.refresh().then(this.open.bind(this));
      }
    }.bind(this));

    document.addEventListener('cart:updated', function () {
      this.refresh().then(this.open.bind(this));
    }.bind(this));

    // The theme's own stock add-to-cart form (module-add-to-cart.js, used on
    // PDPs still running Purity's stock main-product section) publishes its
    // own proprietary pub/sub event instead of a plain "cart:updated"
    // CustomEvent — bridge it so this drawer opens for those too.
    if (window.NextSkyTheme && typeof window.NextSkyTheme.subscribe === 'function' && window.NextSkyTheme.PUB_SUB_EVENTS) {
      window.NextSkyTheme.subscribe(window.NextSkyTheme.PUB_SUB_EVENTS.cartUpdate, function () {
        this.refresh().then(this.open.bind(this));
      }.bind(this));
    }

    this.refresh();
  }

  open() {
    this.classList.add('show');
    this.panel.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.classList.remove('show');
    this.panel.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  refresh() {
    var self = this;
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        self.cart = cart;
        self.renderItems(cart);
        self.renderSuggestions(cart);
        self.updateCountBadges(cart);
        return cart;
      });
  }

  updateCountBadges(cart) {
    document.querySelectorAll('[data-cart-count], .bag-count, #cart-icon-bubble .cart-count .count').forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    document.querySelectorAll('#cart-icon-bubble .cart-count').forEach(function (el) {
      el.classList.toggle('hidden', cart.item_count === 0);
    });
    document.querySelectorAll('a.icon-btn[href="/cart"]').forEach(function (el) {
      el.setAttribute('aria-label', 'Bag, ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items'));
    });
    document.dispatchEvent(new CustomEvent('cart:count-updated', { detail: { count: cart.item_count } }));
  }

  renderItems(cart) {
    var self = this;
    if (!cart.items.length) {
      this.body.innerHTML = '<div class="mcd-empty">Your cart is empty.</div>';
      this.subtotalEl.textContent = this.formatMoney(0);
      return;
    }
    this.subtotalEl.textContent = this.formatMoney(cart.items_subtotal_price);
    this.body.innerHTML = cart.items.map(function (item, i) {
      var variantLine = (item.variant_title && item.variant_title !== 'Default Title')
        ? '<div class="mcd-item-variant">' + item.variant_title + '</div>' : '';
      return (
        '<div class="mcd-item" data-line="' + (i + 1) + '">' +
          '<div class="mcd-item-media">' + (item.image ? '<img src="' + item.image + '" alt="" loading="lazy">' : '') + '</div>' +
          '<div class="mcd-item-info">' +
            '<div class="mcd-item-title">' + item.product_title + '</div>' +
            variantLine +
            '<div class="mcd-item-row">' +
              '<div class="mcd-item-qty">' +
                '<button type="button" data-mcd-decrease>−</button>' +
                '<span>' + item.quantity + '</span>' +
                '<button type="button" data-mcd-increase>+</button>' +
              '</div>' +
              '<div class="mcd-item-price">' + self.formatMoney(item.final_line_price) + '</div>' +
            '</div>' +
            '<button type="button" class="mcd-item-remove" data-mcd-remove>Remove</button>' +
          '</div>' +
        '</div>'
      );
    }).join('');

    this.body.querySelectorAll('[data-mcd-decrease]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var line = parseInt(e.target.closest('[data-line]').dataset.line, 10);
        var item = self.cart.items[line - 1];
        self.changeLine(line, Math.max(0, item.quantity - 1));
      });
    });
    this.body.querySelectorAll('[data-mcd-increase]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var line = parseInt(e.target.closest('[data-line]').dataset.line, 10);
        var item = self.cart.items[line - 1];
        self.changeLine(line, item.quantity + 1);
      });
    });
    this.body.querySelectorAll('[data-mcd-remove]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        var line = parseInt(e.target.closest('[data-line]').dataset.line, 10);
        self.changeLine(line, 0);
      });
    });
  }

  changeLine(line, quantity) {
    var self = this;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { return self.reconcileAutoGifts(cart); })
      .then(function (cart) {
        self.cart = cart;
        self.renderItems(cart);
        self.renderSuggestions(cart);
        self.updateCountBadges(cart);
      });
  }

  // A Dresser's "auto_add_product" setting (sections/cot-bed-product.liquid)
  // pushes a free Changing Pad into the cart alongside the Dresser, tagged
  // with a `_auto_gift_of` line item property pointing at the Dresser's
  // variant id (see assets/product-page.js). After any change to this cart,
  // check whether any gift line's parent variant has disappeared and, if so,
  // remove that gift line too, so the free item never survives on its own.
  reconcileAutoGifts(cart) {
    var self = this;
    var orphanLine = null;
    for (var i = 0; i < cart.items.length; i++) {
      var parentVariantId = cart.items[i].properties && cart.items[i].properties._auto_gift_of;
      if (!parentVariantId) continue;
      var parentStillInCart = cart.items.some(function (item) {
        return String(item.variant_id) === String(parentVariantId);
      });
      if (!parentStillInCart) {
        orphanLine = i + 1;
        break;
      }
    }
    if (!orphanLine) return Promise.resolve(cart);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: orphanLine, quantity: 0 })
    })
      .then(function (r) { return r.json(); })
      .then(function (nextCart) { return self.reconcileAutoGifts(nextCart); });
  }

  renderSuggestions(cart) {
    var self = this;
    if (!cart.items.length) { this.suggestWrap.hidden = true; return; }
    var lastItem = cart.items[cart.items.length - 1];
    fetch('/recommendations/products.json?product_id=' + lastItem.product_id + '&limit=4&intent=related')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data.products || []).filter(function (p) {
          return !cart.items.some(function (i) { return i.product_id === p.id; });
        });
        if (!products.length) { self.suggestWrap.hidden = true; return; }
        self.suggestWrap.hidden = false;
        self.suggestGrid.innerHTML = products.slice(0, 4).map(function (p) {
          var variantId = p.variants && p.variants[0] ? p.variants[0].id : null;
          var media = p.featured_image ? '<img src="' + p.featured_image + '" alt="" loading="lazy">' : '';
          var addBtn = variantId ? '<button type="button" class="mcd-suggest-add" data-variant-id="' + variantId + '" aria-label="Quick add">+</button>' : '';
          return (
            '<div class="mcd-suggest-card-wrap" style="position:relative;">' +
              '<a class="mcd-suggest-card" href="' + p.url + '">' +
                '<div class="mcd-suggest-media">' + media + '</div>' +
                '<div class="mcd-suggest-info">' +
                  '<span class="mcd-suggest-title">' + p.title + '</span>' +
                  '<div class="mcd-suggest-price">' + self.formatMoney(p.price) + '</div>' +
                '</div>' +
              '</a>' +
              addBtn +
            '</div>'
          );
        }).join('');

        self.suggestGrid.querySelectorAll('.mcd-suggest-add').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            self.quickAdd(btn.dataset.variantId);
          });
        });
      })
      .catch(function () { self.suggestWrap.hidden = true; });
  }

  quickAdd(variantId) {
    var self = this;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) { return r.json(); })
      .then(function () { self.refresh(); })
      .catch(function (err) { console.error('Quick add failed', err); });
  }
}
customElements.define('mini-cart-drawer', MiniCartDrawer);
